import React, { FC, useMemo } from "react";
import _find from "lodash/find";
import _map from "lodash/map";
import {
  facetToOption,
  optionToFacet,
  ToggleSwitch,
  QuestionTooltip,
  MultiSelectList,
} from "component-library";
import { useTranslation } from "i18n";
import { ControlSection } from "shared/modules/Common";
import {
  BasicInfoViewModel,
  FacetCategoryViewModel,
} from "shared/modules/BasicInfo/models/views";

const locationsLimit = 10;
const europeCategoryFacetCode = "visasAndPermitsEurope_visasAndPermitsEurope";
const europeCategoryCode = "visasAndPermitsEurope_visasAndPermitsEurope_Europe";

interface Props {
  visasAndPermitsFacets: FacetCategoryViewModel;
  model: BasicInfoViewModel;
  handleChange: (model: BasicInfoViewModel) => void;
}

const VisasAndPermits: FC<React.PropsWithChildren<Props>> = ({
  visasAndPermitsFacets,
  model,
  handleChange,
}) => {
  const { t } = useTranslation();

  const { visasAndPermits = [], isVisibleVisasAndPermits } = model;

  const europeCategoryId = useMemo(
    () =>
      _find(visasAndPermitsFacets.facets, {
        code: europeCategoryCode,
      })?.id,
    [visasAndPermitsFacets]
  );

  const europeCategoryFacetIds = useMemo(
    () =>
      visasAndPermitsFacets.facets.reduce(
        (acc, { code, id }) =>
          code.includes(europeCategoryFacetCode) && id !== europeCategoryId
            ? [...acc, id]
            : acc,
        []
      ),
    [visasAndPermitsFacets]
  );

  const isEuropeanCategorySelected = useMemo(() => {
    return visasAndPermits.some(({ id }) => id === europeCategoryId);
  }, [visasAndPermits]);

  const visasAndPermitsOptions = useMemo(() => {
    return visasAndPermitsFacets.facets.map(facetToOption);
  }, [visasAndPermits]);

  const visasAndPermitsValue = useMemo(
    () => _map(visasAndPermits, facetToOption),
    [visasAndPermits]
  );

  const loadData = (searchString: string) => {
    return Promise.resolve(
      visasAndPermitsOptions.filter(({ name, value }) => {
        const loadedData =
          name.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) &&
          !visasAndPermitsValue.some(
            ({ value: selectedValue }) => selectedValue === value
          );

        return isEuropeanCategorySelected
          ? loadedData && !europeCategoryFacetIds.includes(value)
          : loadedData;
      })
    );
  };

  const handleChangeVisasAndPermits = (values) => {
    const isEuropeCategorySelected = values.some(
      ({ value }) => value === europeCategoryId
    );

    const visasAndPermits = isEuropeCategorySelected
      ? values.filter(({ value }) => !europeCategoryFacetIds.includes(value))
      : values;

    handleChange({
      ...model,
      visasAndPermits: visasAndPermits.map(optionToFacet),
    });
  };

  return (
    <ControlSection name={t("common:profile.visasAndPermits.title")}>
      <p className="flex items-center justify-between mb-5 md:w-1/2 md:pr-[19px]">
        {t("common:profile.visasAndPermits.description", {
          quantity: locationsLimit,
        })}{" "}
        <QuestionTooltip
          content={t("common:profile.visasAndPermits.description.hint")}
        />
      </p>
      <div className="sm:w-full pr-0 md:w-1/2 md:pr-[19px]">
        <MultiSelectList
          type="autocomplete"
          content={{
            label: t("common:profile.visasAndPermits.label"),
            placeholder: t("common:placeholder.start.typing"),
          }}
          isDisabled={visasAndPermits.length >= locationsLimit}
          selectedItems={visasAndPermitsValue}
          name="visasAndPermits"
          loadData={loadData}
          handleChanges={handleChangeVisasAndPermits}
        />
      </div>
      <div>
        <ToggleSwitch
          onChange={(e) =>
            handleChange({
              ...model,
              isVisibleVisasAndPermits: e.target.checked,
            })
          }
          checked={isVisibleVisasAndPermits}
          label={
            <div>
              <p className="font-semibold">
                {t("common:profile.visasAndPermits.visibility.label")}
              </p>
              <p className="text-s">
                {t("common:profile.visasAndPermits.visibility.hint")}
              </p>
            </div>
          }
          className="mt-10 mb-10 md:mt-0"
        />
      </div>
    </ControlSection>
  );
};

export default VisasAndPermits;
